import { RootRoute } from "@app/const";
import {
    ConfigViewType,
    ItemListView,
    NodeTypeName
} from "@app/types";
import BaseNodeModel from "./BaseNodeModel";
import { renderByRoute, updateLastChange } from "../utilities";

export default class IntentDataModel extends BaseNodeModel {
    get _serializedIntent() {
        return window.apm.serialized.components.intents.find(intent => intent.id === this.id)
    }

    /**
     * Intent data model
     * @module IntentDataModel
     * @param {string} nodeName Node display name
     * @param {number} x Node x coordinate on canvas
     * @param {number} y Node y coordinate on canvas
     * @param {Object} props Pre-defined node properties; if non-null, apply values to node properties
     * @param {string} id If non-null, use this ID as the node ID
     */
    constructor(nodeName = "UNTITLED_NODE", x = 0, y = 0, props = null, id = null) {
        super(nodeName, x, y, null, id);
        this.props.nodeTypeName = NodeTypeName.intent;
        this.props.colorClass = "intent";
        this.props.iconClass = "radio";
        this.DEFAULT_COLOR_CLASS = "intent";
        this.type = "intent";
        this.applyProps(props);
    }

    /** @inheritdoc */
    nodeTypeName() {
        const prefix = this._serializedIntent?.isGlobalIntent ? 'Global ' : '';
        return `${prefix} ${this.getProperty('nodeTypeName')}`;
    }

    setConfig(key, value) {
        const { timestamp } = window.apm;
        if (this.props.config[key] !== value) {
            updateLastChange(this.props);
        }
        var oldIntents = null, oldCategories = null;
        if (key === "intents" || key === "categories") {
            oldIntents = this.getConfig("intents") ? JSON.parse(JSON.stringify(this.getConfig("intents"))) : [];
            oldCategories = this.getConfig("categories") ? JSON.parse(JSON.stringify(this.getConfig("categories"))) : [];
        }
        if (key === "dtmfInput" && value === "None") {
            this.setConfig("inputType", "None");
        }
        if (key === "parentIntent") {
            const { intents } = window.apm.serialized.components;

            // Remove child from old parent intent.
            const oldParentIntent = intents.find(({ id }) => id === this.props.config.parentIntent);
            if (oldParentIntent) {
                oldParentIntent.props.config.childIntents ??= [];
                const childIndex = oldParentIntent.props.config.childIntents.findIndex((id) => id === this.id);
                if (childIndex !== -1) {
                    oldParentIntent.props.config.childIntents.splice(childIndex, 1);
                    oldParentIntent.props.lastLocalChange = timestamp;
                }
            }

            // Add child to new parent intent.
            const newParentIntent = intents.find(({ id }) => id === value);
            if (newParentIntent) {
                newParentIntent.props.config.childIntents ??= [];
                const childIndex = newParentIntent.props.config.childIntents.findIndex((id) => id === this.id);
                if (childIndex === -1) {
                    newParentIntent.props.config.childIntents.push(this.id);
                    newParentIntent.props.lastLocalChange = timestamp;
                }
            }
        }

        if (key === "childIntents") {
            const { intents } = window.apm.serialized.components;

            // Remove parent from old child intents.
            this.props.config.childIntents?.forEach(ch => {
                const oldChildIntent = intents.find(({ id }) => id === ch);
                if (oldChildIntent) {
                    oldChildIntent.props.config.parentIntent = "";
                    oldChildIntent.props.lastLocalChange = timestamp;
                }
            });

            // Add parent to new child intents.
            value.forEach(ch => {
                const newChildIntent = intents.find(i => i.id === ch);
                if (newChildIntent) {
                    newChildIntent.props.config.parentIntent = this.id;
                    newChildIntent.props.lastLocalChange = timestamp;
                }
            });
        }
        super.setConfig(key, value);
        if (key === "intents") {
            const treeL = $("#tl_container");
            const children = this.getConfig("intents");
            if (children && children.length) {
                this.setProperty("iconClass", "tag");
                this.setProperty("nodeTypeName", NodeTypeName.intentCategory);
            } else {
                this.setProperty("iconClass", "radio");
                this.setProperty("nodeTypeName", NodeTypeName.intent);
            }
            document.dispatchEvent(new CustomEvent("ev-property-refresh"));
            const n = treeL.jstree().get_node(this.id);
            treeL.jstree().set_icon(n, this.getProperty("iconClass"));
        }
        if (key === "intents" || key === "categories") {
            this.updateReferences(oldIntents, oldCategories);
        }
        document.dispatchEvent(new CustomEvent("ev-property-refresh"));
        renderByRoute(RootRoute.intents);

    }

    updateReferences(oldIntents = null, oldCategories = null) {
        if (oldIntents || oldCategories) {
            this.removeReferences(oldIntents, oldCategories);
        }
        if (this.getConfig("intents")) {
            this.setReferences(this.getConfig("intents"), "set", "intents");
        }
        if (this.getConfig("categories")) {
            this.setReferences(this.getConfig("categories"), "set", "categories");
        }
    }
    removeReferences(oldIntents = null, oldCategories = null) {
        var intents = oldIntents, categories = oldCategories;
        if (this.getConfig("intents") && oldIntents === null) {
            intents = this.getConfig("intents");
        }
        if (this.getConfig("categories") && oldCategories === null) {
            categories = this.getConfig("categories");
        }
        if (intents) {
            this.setReferences(intents, "del", "intents");
        }
        if (categories) {
            this.setReferences(categories, "del", "categories");
        }
    }

    setReferences(refs, op, type) {
        for (var i = 0; i < refs.length; i++) {
            const r = refs[i];
            if (op === "set") {
                window.apm.setIntentRef(this.id, r, type);
            }
            if (op === "del") {
                window.apm.removeIntentRef(this.id, r, type);
            }
        }
    }
    getIntents(intent) {
        var intents = [];
        window.apm.serialized.globalIntents.forEach(gi => {
            if ((window.apm.knowledgeBase.getIntents().findIndex(i => i.value === gi.intent)) !== -1) {
                intents.push(window.apm.knowledgeBase.getIntents()[window.apm.knowledgeBase.getIntents().findIndex(i => i.value === gi.intent)]);
            }
        })
        var currentIndex = intents.findIndex(ele => ele.value === intent.id);
        intents.splice(currentIndex, 1);
        if (intent.props.config.childIntents && intent.props.config.childIntents.length > 0) {
            for (var ch = 0; ch < intent.props.config.childIntents.length; ch++) {
                var childIntent = intents.findIndex(ele => ele.value === intent.props.config.childIntents[ch]);
                if (childIntent !== -1) {
                    intents.splice(childIntent, 1);
                }
            }

        }
        return intents.sort((a, b) => {
            return a.name.localeCompare(b.name);
        });
    }
    getConfigViews() {
        const hasDTMF = this.props.config.dtmfInput && this.props.config.dtmfInput.length === 1;
        const isNameLocked = this.getConfig("lockName");
        const {
            isGlobalIntent = false,
            props: {
                config: { inARow, xAndOut } = {}
            } = {}
        } = this._serializedIntent ?? {};
        const nameField = this.getConfigViewNameField(isNameLocked);
        const intentHandlingTooltip = "In-a-row and X-and-out handling modules can be set on the settings page.";
        const globalDTMFTooltip = "The DTMF character that will trigger this intent at any Input node in the application";
        const configView = [
            ...nameField,
            {   type: "Textarea", 
                label: this.getPropLabel("description"), 
                key: "description" 
            },
            { type: "hr" },
            {
                type: "Dropdown", 
                key: "dtmfInput", 
                label: "Global DTMF", 
                optionName: "Select DTMF",
                options: window.apm.knowledgeBase.getDTMFOptions(this),
                tooltip: globalDTMFTooltip
            }
        ];

        if(hasDTMF){
            const limitToInputTypeTooltip = "Limit the Global DTMF configuration to a single Input Type in the application";
            const inputType = {
                type: "Dropdown",
                key: "inputType",
                label: "Limit to Input Type",
                optionName: this.getPropLabel("input_type_opt"),
                options: window.apm.knowledgeBase.getInputTypes(),
                tooltip: limitToInputTypeTooltip
            };
            configView.push(inputType);
        }

        configView.push(
            { type: "hr" },
            { type: "Label", labelName: this.getPropLabel("intent_handling"), key: "intentHandling" },
            { type: "Switch", key: "inARow", label: this.getPropLabel("in_a_row_watched"), tooltip: intentHandlingTooltip, checked: inARow },
            { type: "Switch", key: "xAndOut", label: this.getPropLabel("x_and_out_watched"), tooltip: intentHandlingTooltip, checked: xAndOut },
            { type: "hr" }
        );

        if (isGlobalIntent) {
            configView.push({
                type: "Dropdown",
                label: this.getPropLabel("parent_intent"),
                key: "parentIntent",
                optionName: "Select Intent",
                options: this.getIntents(this),
                removable: true
            });
            configView.push({ type: ConfigViewType.oldItemList, label: this.getPropLabel("child_intents"), key: "childIntents", view: ItemListView.localIntent });
            configView.push({ type: ConfigViewType.oldItemList, label: this.getPropLabel("example_phrases"), key: "examplePhrases", view: ItemListView.examplePhrases });
            configView.push({ type: "hr" });
        }

        configView.push({ type: "Switch", key: "droppable", label: this.getPropLabel("droppable"), checked: false });
        configView.push({ type: "hr" });
        configView.push({ type: "Textarea", label: this.getPropLabel("note"), key: "note" });

        return configView;
    }

    /**
     * Tells canvas to skip some calculations only applicable to general nodes
     * @returns {boolean}
     */
    isSpecialNode() {
        return true;
    }
}